import { useMemo, useState } from 'react'
import { CapabilityAction, EligibleMember, useClubQuotas, useEligibleMembers } from '@/services/clubCapabilities'
import { CMButton, Spinner } from '../ui'
import { ActionMember, MemberActionModal } from '../tools/MemberActionModal'
import { toCoachingCopy } from './coachingCopy'

type Props = {
  clubId: number
  userId: number
  /** Used to look this member up in the (paginated) eligibility listing. */
  duprId?: string | null
  fullName: string
  capabilityKey: string
  action: CapabilityAction
}

const asText = (rating: string | number | null) => (rating != null ? String(rating) : null)

// The backend stores "unlimited" credits as a large int sentinel (Int.MAX ≈ 2.1e9). Anything at or
// above this threshold is shown as "Unlimited" rather than the raw number.
const UNLIMITED_CREDITS = 100_000

/**
 * One coaching action for a single member, shown inside the member view. It resolves the member's
 * live eligibility for the capability (looked up by DUPR id in the eligibility listing) and renders
 * a verdict: eligible members get the action button; ineligible ones see the reason(s) inline.
 * Wording is softened to the coach-facing "tune" vocabulary via {@link toCoachingCopy}.
 */
export const CoachingToolCard = ({ clubId, userId, duprId, fullName, capabilityKey, action }: Props) => {
  const [open, setOpen] = useState(false)

  // Targeted lookup of just this member's verdict. The ES eligibility index is searched by name, so
  // query by full name (DUPR id isn't a searchable field) and then pick this member out by userId.
  const eligibilityQuery = useEligibleMembers(
    clubId,
    capabilityKey,
    { offset: 0, limit: 25, query: fullName || duprId || '' },
    true
  )

  const row: EligibleMember | undefined = useMemo(
    () => eligibilityQuery.data?.hits?.find((m) => m.userId === userId),
    [eligibilityQuery.data, userId]
  )

  // Club-level credit balances per credit TYPE (one row each). Club-scoped and cached by clubId, so
  // it's shared across every coaching card. Shown as a compact "remaining of allocated" per type.
  const quotasQuery = useClubQuotas(clubId, true)
  const quotas = (quotasQuery.data ?? []).filter((q) => q.allocated > 0)

  const title = toCoachingCopy(action.label)
  const description = action.description ? toCoachingCopy(action.description) : undefined

  const currentValues = {
    singles: asText(row?.singles ?? null),
    doubles: asText(row?.doubles ?? null),
  }

  // Trust the eligibility API's verdict. The ES-backed eligibility search returns every member's
  // verdict for this capability (eligible or not, with reasons); a member who isn't returned at all
  // — e.g. yourself / a director who can't be tuned — is not eligible. Don't default to eligible.
  const eligible = row?.eligible ?? false
  const reasons = (row?.reasons ?? []).map(toCoachingCopy)

  const status = (() => {
    if (eligibilityQuery.isLoading) return { tone: 'muted', text: 'Checking eligibility…' }
    if (eligibilityQuery.isError) return { tone: 'blocked', text: 'Eligibility unavailable' }
    if (eligible) return { tone: 'ok', text: 'Eligible' }
    return { tone: 'blocked', text: reasons.join(' · ') || 'Not eligible right now' }
  })()

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
          <p
            className={
              'mt-1 flex items-center gap-1.5 text-xs ' +
              (status.tone === 'ok' ? 'text-green-600' : status.tone === 'blocked' ? 'text-gray-500' : 'text-gray-400')
            }
          >
            {status.tone === 'ok' && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden />
            )}
            {status.tone === 'blocked' && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-300" aria-hidden />
            )}
            <span className="truncate">{status.text}</span>
          </p>
        </div>

        <div className="shrink-0">
          {eligibilityQuery.isLoading ? (
            <Spinner size="sm" />
          ) : (
            <CMButton variant="primary" size="sm" disabled={!eligible} onClick={() => setOpen(true)}>
              {title}
            </CMButton>
          )}
        </div>
      </div>

      {quotas.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-gray-100 pt-2">
          {quotas.map((q) => {
            // The backend models "unlimited" as a very large int sentinel (Int.MAX). Render it as
            // the word rather than the raw number, and collapse "N of Unlimited" to just "Unlimited".
            const unlimited = q.allocated >= UNLIMITED_CREDITS
            return (
              <span key={`${q.resourceUrn}:${q.holderUrn ?? ''}`} className="text-[11px] text-gray-500">
                {toCoachingCopy(q.label)}:{' '}
                {unlimited ? (
                  <span className="font-medium text-gray-900">Unlimited</span>
                ) : (
                  <>
                    <span className="font-medium text-gray-900">{q.remaining}</span> of {q.allocated}
                  </>
                )}
              </span>
            )
          })}
        </div>
      )}

      {open && (
        <MemberActionModal
          open={open}
          onClose={() => setOpen(false)}
          clubId={clubId}
          capabilityKey={capabilityKey}
          action={action}
          member={{ userId, fullName, currentValues } as ActionMember}
          titleOverride={title}
          descriptionOverride={description}
          submitLabel={title}
          formatCopy={toCoachingCopy}
        />
      )}
    </div>
  )
}
